import { useQuery } from '@tanstack/react-query';
import { apiFetch } from './client';
import type { ForageFindDto } from './forage';

/**
 * `GET /api/v1/world/{landCode}/freebie` — the daily gift as a named thing (docs/SCREENS.md
 * "Welcome Basket", server: `world/FreebieStateDto.java`).
 *
 * Separate from `api/world.ts`'s `useClaimFreebie` mutation on purpose, exactly as `api/forage.ts`
 * is separate from `useForage`, and for the same reason: that one POSTs and lives beside the world
 * graph it invalidates, while this is a per-land read only one sheet ever wants.
 *
 * WHY THIS READ EXISTS AT ALL: before it, the sheet could not tell whether today's gift had
 * already been taken without attempting to take it — the only route to "come back tomorrow" was
 * firing a claim and catching the server's 409. Nothing on the world snapshot carries
 * claimed-state.
 */

/** A cell of the "What's inside" board. Structurally identical to a forage board cell — the same
 *  server record backs both (`ForageFindsDto.FindDto`) — so the two boards can share one renderer
 *  instead of drifting apart. Re-exported under the basket's own name so this module reads on its
 *  own terms; it is deliberately the same type, not a copy. */
export type FreebieFindDto = ForageFindDto;

/** One collection set a basket feeds.
 *
 *  `owned`/`total` count pieces CURRENTLY IN THE BAG — the same definition the collections screen
 *  and the Journal use, so this number can never disagree with those. That is a different question
 *  from a board cell's `found` ("ever found here", which survives eating or selling the piece);
 *  `FreebieStateDto.SetProgressDto`'s javadoc has the full reasoning for keeping them apart. */
export type FreebieSetProgressDto = {
  code: string;
  name: string;
  owned: number;
  total: number;
  completed: boolean;
};

export type FreebieStateDto = {
  activityCode: string;
  /** The activity's own authored name — "The Welcome Basket", "The Driftwood Cache". Six named
   *  places that all rendered as the string "Daily freebie" before this shipped. */
  basketName: string;
  landCode: string;
  landName: string;
  landDescription: string | null;
  claimedToday: boolean;
  /** ISO instant, ALWAYS set — the next UTC calendar-day boundary, whether claimed or not, because
   *  the sheet shows the countdown in both states. This is what lets the client stop computing the
   *  reset locally, which it used to have to do because the 409 carries no body. */
  nextResetAt: string;
  contents: FreebieFindDto[];
  sets: FreebieSetProgressDto[];
};

export const freebieKeys = {
  all: ['freebie'] as const,
  state: (landCode: string) => ['freebie', 'state', landCode] as const,
};

export const getFreebieState = (landCode: string) =>
  apiFetch<FreebieStateDto>(`/world/${landCode}/freebie`);

export function useFreebieState(landCode: string) {
  return useQuery({
    queryKey: freebieKeys.state(landCode),
    queryFn: () => getFreebieState(landCode),
    enabled: landCode !== '',
  });
}
